import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiShield, FiSearch, FiHome, FiBuilding } = FiIcons;

const HMSCompanySelection = () => {
  const navigate = useNavigate();
  const { companies } = useData();
  const { login } = useAuth();
  const [companySearch, setCompanySearch] = useState('');
  const [preSelectedCompany, setPreSelectedCompany] = useState(null);

  // Check if coming from company dashboard
  useEffect(() => {
    const hmsCompanyId = sessionStorage.getItem('hmsCompanyId');
    const hmsCompanyName = sessionStorage.getItem('hmsCompanyName');
    if (hmsCompanyId && hmsCompanyName) {
      // Pre-select the company
      setPreSelectedCompany({ id: hmsCompanyId, name: hmsCompanyName });
      setCompanySearch(hmsCompanyName);
    }
  }, []);

  // Filter companies based on search
  const filteredCompanies = companies.filter(c =>
    c.active && c.name.toLowerCase().includes(companySearch.toLowerCase())
  );

  const handleCompanySelect = (companyId) => {
    const selectedCompany = companies.find(c => c.id === companyId);
    
    // Set up HMS reporter session
    const userData = {
      companyId: companyId,
      companyName: selectedCompany?.name,
      reporterType: 'hms',
      isAnonymous: false
    };
    login(userData, 'hms');
    
    // VIKTIG ENDRING: Gå til valgsiden (HMS vs RUH) i stedet for rett til skjemaet
    navigate('/hms/select-type');
  };

  // Handle quick selection if coming from company dashboard
  const handleQuickSelect = () => {
    if (preSelectedCompany) {
      handleCompanySelect(preSelectedCompany.id);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Navigation Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors">
              <SafeIcon icon={FiHome} />
              <span>Tilbake til forsiden</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiShield} className="text-white text-sm" />
              </div>
              <span className="font-semibold text-gray-900">HMS Rapportering</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-5xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiShield} className="text-white text-2xl" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Finn din bedrift</h1>
            <p className="text-gray-600">
              {preSelectedCompany 
                ? `Rapporter hendelse for ${preSelectedCompany.name}` 
                : 'Søk opp bedriften du jobber i for å starte rapportering'}
            </p>
          </div>

          {/* Quick Action for Pre-selected Company */}
          {preSelectedCompany && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8 text-center"
            >
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <SafeIcon icon={FiBuilding} className="text-white text-3xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{preSelectedCompany.name}</h3>
                  <p className="text-gray-600">Du er logget inn som ansatt</p>
                </div>
                <button
                  onClick={handleQuickSelect}
                  className="mt-4 bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 transition-colors font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Start Rapportering
                </button>
              </div>
            </motion.div>
          )}

          {/* Company Selection (Only if not pre-selected) */}
          {!preSelectedCompany && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-8"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Søk etter bedrift *
                </label>
                {/* Search Field */}
                <div className="relative mb-4">
                  <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Skriv bedriftsnavn..."
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Company List */}
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                  {filteredCompanies.length > 0 ? (
                    filteredCompanies.map(company => (
                      <motion.div
                        key={company.id}
                        whileHover={{ backgroundColor: '#f9fafb' }}
                        className="p-6 border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50"
                        onClick={() => handleCompanySelect(company.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            {company.branding?.logo ? (
                              <img src={company.branding.logo} alt={`${company.name} logo`} className="h-12 w-auto" />
                            ) : (
                              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: company.branding?.primaryColor || '#2563EB' }}>
                                <SafeIcon icon={FiBuilding} className="text-white text-xl" />
                              </div>
                            )}
                            <div>
                              <h3 className="font-medium text-gray-900 text-lg">{company.name}</h3>
                              <p className="text-sm text-gray-500">
                                {company.industry} • {company.employees} ansatte
                              </p>
                            </div>
                          </div>
                          <SafeIcon icon={FiShield} className="text-gray-400 text-xl" />
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      {companySearch ? 'Ingen bedrifter funnet' : 'Begynn å skrive for å søke'}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Privacy Notice */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>
              HMS-rapporter behandles konfidensielt og brukes til læring og forbedring av arbeidsmiljøet.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HMSCompanySelection;